import { MapPin, Mail, Phone, Linkedin, Instagram, Facebook } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
const Footer = () => {
  const {
    t
  } = useLanguage();
  const quickLinks = [{
    name: t("footer.about"),
    href: "/#about"
  }, {
    name: t("header.partners"),
    href: "/#partners"
  }, {
    name: "Procurement",
    href: "/procurement"
  }, {
    name: t("footer.contact"),
    href: "/contact"
  }];
  const services = [
    { name: "About", href: "/about" },
    { name: "Pillars", href: "/pillars" },
    { name: "Homes", href: "/portfolio/homes" },
    { name: "Hotels", href: "/portfolio/hotels" },
    { name: "Developments", href: "/portfolio/developments" },
    { name: "Technologies", href: "/portfolio/technologies" },
    { name: "Careers", href: "/careers" },
    { name: "Collaborators", href: "/collaborators" },
    { name: "News & Press", href: "/news-press" },
    { name: "Procurement", href: "/procurement" },
    { name: "FAQ", href: "/faq" },
    { name: "Contact", href: "/contact" }
  ];
  return <footer className="bg-background border-t border-border/50">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 md:py-12 lg:py-16">
        {/* All sections in one horizontal row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-3 sm:mb-4">
              <img 
                src="/bhg-logo.png" 
                alt="BHG Logo" 
                className="h-5 sm:h-6 mr-2" 
                width="120"
                height="24"
                loading="lazy"
              />
            </div>
            <p className="text-muted-foreground leading-relaxed mb-3 sm:mb-4 text-xs sm:text-sm">
              {t("footer.description")}
            </p>
            <div className="flex space-x-2 sm:space-x-3">
              <a 
                href="https://www.linkedin.com/company/brassbellhospitalitygroup/?viewAsMember=true" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Visit Brassbell Hospitality Group on LinkedIn"
              >
                <Linkedin size={16} className="sm:w-4 sm:h-4" />
              </a>
              <a 
                href="https://www.instagram.com/brassbellhospitalitygroup?igsh=MWhqa2MzaDM3cnFuNQ==" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Follow Brassbell Hospitality Group on Instagram"
              >
                <Instagram size={16} className="sm:w-4 sm:h-4" />
              </a>
              <a 
                href="https://www.facebook.com/brassbellhospitalitygroup" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Like Brassbell Hospitality Group on Facebook"
              >
                <Facebook size={16} className="sm:w-4 sm:h-4" />
              </a>
            </div>
          </div>

          {/* Office Locations */}
          <div>
            <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-xs sm:text-sm">Our Offices</h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start">
                <MapPin className="text-primary mr-1 sm:mr-2 mt-0.5 flex-shrink-0" size={12} />
                <div>
                  <div className="font-medium text-foreground text-xs">Egypt</div>
                  <span className="text-muted-foreground text-xs">Zamalek, Cairo</span>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="text-primary mr-1 sm:mr-2 mt-0.5 flex-shrink-0" size={12} />
                <div>
                  <div className="font-medium text-foreground text-xs">Saudi Arabia</div>
                  <span className="text-muted-foreground text-xs">Riyadh</span>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="text-primary mr-1 sm:mr-2 mt-0.5 flex-shrink-0" size={12} />
                <div>
                  <div className="font-medium text-foreground text-xs">UAE</div>
                  <span className="text-muted-foreground text-xs">Dubai</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Details */}
          <div>
            <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-xs sm:text-sm">Get in Touch</h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center">
                <Mail className="text-primary mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                <a href="mailto:info@brassbellhospitality.com" className="text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm">
                  info@brassbellhospitality.com
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="text-primary mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                <a href="tel:+201283492222" className="text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm">
                  +20 12 8349 2222
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-xs sm:text-sm">{t("footer.quickLinks")}</h3>
            <ul className="space-y-1 sm:space-y-2">
              {quickLinks.map((link, index) => <li key={index}>
                  <a href={link.href} className="text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm">
                    {link.name}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Pages */}
          <div>
            <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-xs sm:text-sm">Pages</h3>
            <ul className="space-y-1 sm:space-y-2">
              {services.slice(0, 8).map((service, index) => <li key={index}>
                  <a href={service.href} className="text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm">
                    {service.name}
                  </a>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-border/50 pt-6 sm:pt-8 text-center">
          <p className="text-muted-foreground text-xs sm:text-sm">
            Copyright © 2025 Brassbell Hospitality Group. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>;
};
export default Footer;